import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Sparkles, CheckCircle2, ShieldCheck } from 'lucide-react';
import FloatingSymbols from '../components/FloatingSymbols';
import BrandLogo from '../components/BrandLogo';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two-step self-serve signup with mobile-OTP verification.
 *
 *   Step 1 (`form`)  — Name, Mobile, Email, Password → POST /auth/signup/request-otp
 *   Step 2 (`otp`)   — 6-digit code → POST /auth/signup/verify
 *
 * On successful verification we auto-login via useAuth().login() so the
 * AuthContext state is fully populated, then redirect to /dashboard.
 *
 * OTP flow is required because this page is the marketing-CTA target; without
 * mobile verification, bots would burn through trial slots. The stricter
 * legacy /register flow (fuller form, no OTP) stays available for anyone who
 * wants it.
 */
const Signup = () => {
  const [step, setStep] = useState('form');   // 'form' | 'otp'
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [otp, setOtp] = useState('');
  const [phoneLast4, setPhoneLast4] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimer = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Start / tick a resend cooldown countdown after each successful OTP request.
  const startCooldown = (seconds) => {
    setResendCooldown(seconds);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(cooldownTimer.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };
  useEffect(() => () => cooldownTimer.current && clearInterval(cooldownTimer.current), []);

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (!/^[+0-9\-\s]{7,}$/.test(form.phone.trim())) { toast.error('Please enter a valid mobile number'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/signup/request-otp`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
      });
      setPhoneLast4(data.phone_last4 || '');
      setStep('otp');
      startCooldown(60);
      toast.success('OTP sent — check your SMS');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send OTP — please retry');
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) { toast.error('Enter the 6-digit code from your SMS'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/signup/verify`, {
        phone: form.phone.trim(),
        otp: otp.trim(),
      });
      // Rehydrate AuthContext via the standard login flow — we have the
      // password in memory, so no extra prompt for the user.
      const result = await login(form.email.trim().toLowerCase(), form.password);
      if (result.success) {
        toast.success("You're in — 14-day free trial started");
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Verified — but auto sign-in failed. Please sign in manually.');
        navigate('/login');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-slate-50 to-slate-100 p-4 overflow-hidden">
      <FloatingSymbols opacity={0.9} />
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-6">
          <BrandLogo size={44} className="mb-2 justify-center w-full" />
          <p className="text-slate-600">Start your 14-day free trial — no card required</p>
        </div>

        <Card className="border-slate-200 shadow-lg" data-testid="signup-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              {step === 'form' ? <Sparkles className="h-5 w-5 text-emerald-600" /> : <ShieldCheck className="h-5 w-5 text-blue-800" />}
              {step === 'form' ? 'Start Free Trial' : 'Verify your mobile'}
            </CardTitle>
            <CardDescription>
              {step === 'form'
                ? "Full access for 14 days. Add a plan any time — we never charge automatically."
                : `We sent a 6-digit code to your mobile ending ${phoneLast4}. Enter it below to finish signing up.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'form' && (
              <form onSubmit={requestOtp} className="space-y-4" data-testid="signup-form-step">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Full Name *</Label>
                  <Input id="signup-name" value={form.name} onChange={(e) => upd({ name: e.target.value })} required autoComplete="name" data-testid="signup-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-phone">Mobile *</Label>
                  <Input id="signup-phone" type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={(e) => upd({ phone: e.target.value })} required autoComplete="tel" data-testid="signup-phone-input" />
                  <p className="text-[10px] text-slate-500">We'll SMS a 6-digit code to verify this number.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email *</Label>
                  <Input id="signup-email" type="email" value={form.email} onChange={(e) => upd({ email: e.target.value })} required autoComplete="email" data-testid="signup-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password *</Label>
                  <Input id="signup-password" type="password" placeholder="At least 6 characters" value={form.password} onChange={(e) => upd({ password: e.target.value })} required autoComplete="new-password" data-testid="signup-password-input" />
                  <PasswordStrengthMeter password={form.password} testId="signup-password-strength" />
                </div>

                <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium" disabled={loading} data-testid="signup-submit-button">
                  {loading ? 'Sending OTP…' : 'Send verification code'}
                </Button>

                <ul className="text-xs text-slate-500 space-y-1 pt-1" data-testid="signup-benefits">
                  <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-600" />14 days full access — every feature, every screen</li>
                  <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-600" />No credit card. We never auto-charge you</li>
                  <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-600" />Mobile-verified accounts only — no spam neighbours</li>
                </ul>
              </form>
            )}

            {step === 'otp' && (
              <form onSubmit={verifyOtp} className="space-y-4" data-testid="signup-otp-step">
                <div className="space-y-2">
                  <Label htmlFor="signup-otp">6-digit code</Label>
                  <Input
                    id="signup-otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/[^\d]/g, ''))}
                    required
                    data-testid="signup-otp-input"
                    className="text-center text-2xl tracking-widest"
                  />
                </div>
                <Button type="submit" className="w-full bg-blue-800 hover:bg-blue-900 text-white font-medium" disabled={loading || otp.length !== 6} data-testid="signup-verify-button">
                  {loading ? 'Verifying…' : 'Verify & start trial'}
                </Button>
                <div className="flex items-center justify-between text-xs">
                  <button
                    type="button"
                    onClick={() => setStep('form')}
                    className="text-slate-600 hover:text-slate-900"
                    data-testid="signup-back-to-form"
                  >
                    ← Change details
                  </button>
                  <button
                    type="button"
                    onClick={() => requestOtp()}
                    disabled={loading || resendCooldown > 0}
                    className="text-blue-800 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="signup-resend-otp"
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>
              </form>
            )}

            <div className="mt-5 text-center text-sm">
              <span className="text-slate-600">Already have an account? </span>
              <Link to="/login" className="text-blue-800 hover:text-blue-900 font-medium" data-testid="signup-login-link">Sign in</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Signup;
